/**
 * ReactVideoPlayer Component
 * 
 * A React component that uses react-player library loaded from CDN.
 * Supports multiple video formats including YouTube, Vimeo, Facebook, 
 * Twitch, SoundCloud, and more via HLS support.
 * 
 * Usage:
 * <react-component 
 *   name="ReactVideoPlayer" 
 *   props="{
 *     url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
 *     controls: true,
 *     width: '100%',
 *     height: '360px',
 *     playing: false,
 *     muted: false,
 *     loop: false,
 *     volume: 0.8,
 *     onReady: callbackFunction,
 *     onPlay: callbackFunction,
 *     onPause: callbackFunction,
 *     onEnded: callbackFunction
 *   }">
 * </react-component>
 * 
 * CDN: The react-player script should be loaded in your HTML
 */

function ReactVideoPlayer({
  url,
  controls = true,
  width = '100%',
  height = '360px',
  playing = false,
  muted = false,
  loop = false,
  volume = 0.8,
  playbackRate = 1.0,
  pip = false,
  light = false,
  config = {},
  lang = {},
  onReady = () => {},
  onStart = () => {},
  onPlay = () => {},
  onPause = () => {},
  onBuffer = () => {},
  onBufferEnd = () => {},
  onEnded = () => {},
  onError = () => {},
  onProgress = () => {},
  onDuration = () => {},
  onSeek = () => {},
  className = '',
  style = {}
}) {
  console.log('ReactVideoPlayer: Component rendered with URL:', url);
  const containerRef = React.useRef(null);
  const playerInstanceRef = React.useRef(null);
  const [isReady, setIsReady] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Initialize react-player when container is ready
  React.useEffect(() => {
    if (url && typeof url === 'string') {
      if (!containerRef.current) return;

      console.log("ReactVideoPlayer: renderReactPlayer object: ", renderReactPlayer);

      // Check if renderReactPlayer is available
      if (typeof renderReactPlayer === "undefined") {
        setError(
          'react-player not loaded. Please include: <script src="https://unpkg.com/react-player@2.14.1/dist/ReactPlayer.standalone.js"></script>'
        );
        return;
      }

      console.log("ReactVideoPlayer: Initializing player for URL:", url);
      console.log(
        "ReactVideoPlayer: renderReactPlayer function:",
        renderReactPlayer
      );

      // Render the player into our container
      try {
        renderReactPlayer(containerRef.current, {
          url,
          playing,
          controls,
          width: "100%",
          height: "100%",
          muted,
          loop,
          volume,
          playbackRate,
          pip,
          light,
          config,
          onReady: () => {
            setIsReady(true);
            onReady();
          },
          onStart,
          onPlay,
          onPause,
          onBuffer,
          onBufferEnd,
          onEnded: () => {
            setIsReady(false);
            onEnded();
          },
          onError: (err) => {      
            console.log('ReactVideoPlayer: onError callback triggered with error:', err);      
            // Ignore AbortError - this happens when pause() is called while play() is still loading
            // It's expected behavior, not an actual error
            const errorName = err?.name || '';
            const errorMessage = err?.message || err?.toString() || '';
            
            if (errorName === 'AbortError' || 
                errorMessage.includes('interrupted by a call to pause') ||
                errorMessage.includes('AbortError')) {
              return;
            }
            
            console.error("ReactPlayer error:", err);
            setError(errorMessage || "Playback error");
            onError(err);
          },
          onProgress,
          onDuration,
          onSeek,
        });

        playerInstanceRef.current = containerRef.current;
      } catch (err) {
        console.error("Error initializing ReactPlayer:", err);
        setError(err.message || "Failed to initialize player");
      }

      // Cleanup function
      return () => {
        if (containerRef.current) {
          // Clear the container
          while (containerRef.current.firstChild) {
            containerRef.current.removeChild(containerRef.current.firstChild);
          }
        }
      };
    }
  }, url); // Only run once on mount

  // Update player when props change
  React.useEffect(() => {
    if (!containerRef.current || !playerInstanceRef.current) return;

    // Re-render with new props
    if (typeof renderReactPlayer !== 'undefined') {
      renderReactPlayer(containerRef.current, {
        url,
        playing,
        controls,
        width: '100%',
        height: '100%',
        muted,
        loop,
        volume,
        playbackRate,
        pip,
        light,
        config,
        onReady: () => {
          setIsReady(true);
          onReady();
        },
        onStart,
        onPlay,
        onPause,
        onBuffer,
        onBufferEnd,
        onEnded,
        onError: (err) => {          
          // Ignore AbortError - this happens when pause() is called while play() is still loading
          // It's expected behavior, not an actual error
          const errorName = err?.name || '';
          const errorMessage = err?.message || err?.toString() || '';
          
          if (errorName === 'AbortError' || 
              errorMessage.includes('interrupted by a call to pause') ||
              errorMessage.includes('AbortError')) {
            return;
          } else if (err === 'hlsError') {
            // Ignore HLS errors because the video cantinues to play
            return;
          }
          
          console.error('ReactPlayer error:', err);
          setError(errorMessage || 'Playback error');
          onError(err);
        },
        onProgress,
        onDuration,
        onSeek
      });
    }
  }, [url, playing, controls, muted, loop, volume, playbackRate, pip, light, config]);

  // Show "no URL" state
  if (!url || (error && !(error.includes('interrupted by a call to pause') ||
    error.includes('AbortError')))
  ) {
    const errorMessage = lang.__video_playback_error || 'Error! Cannot play this video file';
    
    return (
      <div 
        className={`react-video-player-no-url ${className}`}
        style={{
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f0f0f0',
          border: '1px solid #ddd',
          borderRadius: '4px',
          ...style
        }}
      >
        <p style={{ 
          color: '#999',
          whiteSpace: 'pre-line',
          textAlign: 'center',
          padding: '0 16px'
        }}>
          {errorMessage}
        </p>
      </div>
    );
  }

  // Render the player container
  return (
    <div 
      className={`react-video-player-wrapper ${className}`}
      style={{
        width,
        height,
        position: 'relative',
        backgroundColor: '#000',
        borderRadius: '4px',
        overflow: 'hidden',
        ...style
      }}
    >      
      <div 
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%'
        }}
      />
    </div>
  );
}

// Export for potential standalone use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ReactVideoPlayer;
}


